import React, { useState } from 'react';
import { ChevronDown, Search, HelpCircle } from 'lucide-react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';

const ALL_FAQS = [
    { category: 'general', question: "Czym dokładnie zajmuje się BiznesBezKlikania.pl?", answer: "Pomagamy firmom oszczędzać czas i pieniądze dzięki sztucznej inteligencji. Automatyzujemy obsługę klientów, pozyskiwanie leadów, reklamacje i tworzenie treści marketingowych – tak, żeby biznes działał praktycznie sam." },
    { category: 'general', question: "Czy wasze rozwiązania działają od razu, czy trzeba coś długo wdrażać?", answer: "Większość działa w 48h – wystarczy podstawowa konfiguracja (np. e-mail, Messenger, WhatsApp, konto Google). Potem system sam pracuje w tle." },
    { category: 'general', question: "Czy muszę znać się na AI, żeby z tego korzystać?", answer: "Nie. Wszystko jest przygotowane tak, żeby działało 'od ręki'. Ty tylko korzystasz z efektów – więcej klientów, szybsza obsługa, mniej papierologii." },
    { category: 'agenci', question: "Co to jest Agent WhatsApp & Messenger AI?", answer: "To bot, który odpowiada na pytania klientów 24/7. Może podawać ceny, statusy zamówień, umawiać wizyty, a nawet zamykać sprzedaż – bez udziału człowieka." },
    { category: 'agenci', question: "Jak działa SmartClaim AI?", answer: "System czyta wiadomości od klientów, rozpoznaje reklamacje i sam odpowiada zgodnie z zasadami sklepu. Dzięki temu klient dostaje szybką decyzję, a Ty oszczędzasz godziny pracy." },
    { category: 'leady', question: "Jak działa BiznesFinder AI?", answer: "Wpisujesz rodzaj firmy (np. fryzjerzy w Krakowie), a system sam zbiera kontakty z Google Maps i zapisuje je w arkuszu. Masz gotową bazę klientów do kontaktu." },
    { category: 'leady', question: "Co robi CarHunter AI?", answer: "Przeszukuje portale z ogłoszeniami (mobile.de) i wyłapuje okazje – np. auta z uszkodzonym silnikiem, które można tanio kupić." },
    { category: 'leady', question: "Czym jest SizeHunter AI?", answer: "To porównywarka cen i dostępności butów w 150+ sklepach. Wpisujesz rozmiar i markę, a AI pokazuje, gdzie kupisz najtaniej i czy rozmiar jest dostępny." },
    { category: 'leady', question: "Po co mi NewsHunter AI?", answer: "Codziennie analizuje setki artykułów i wybiera te, które mają największy potencjał na social media. Dzięki temu masz zawsze świeże treści na FB, LinkedIn czy Instagramie." },
    { category: 'content', question: "Co robi ContentFlow AI?", answer: "Codziennie generuje posty, grafiki i shorty wideo dopasowane do Twojej branży. Samo publikuje je na Facebooku, LinkedIn, Instagramie i X. Zero wymówek 'nie mam czasu na content'." },
    { category: 'content', question: "Na czym polega Visual Generator AI?", answer: "To narzędzie, które w kilka sekund stworzy profesjonalne grafiki i filmy na social media. Możesz zrobić setki postów miesięcznie, bez grafika." },
    { category: 'content', question: "Czym był Amazon Lister AI?", answer: "To nasz MVP, który automatycznie tworzył opisy i grafiki produktów, a potem wystawiał aukcje na Amazonie. Projekt zamknięty, ale pokazał, jak szybko AI może zastąpić ręczną pracę." },
    { category: 'finanse', question: "Jak działa PayPal Sync AI?", answer: "Automatycznie synchronizuje płatności z PayPala z Twoim systemem (np. księgowość, CRM). Koniec z ręcznym przepisywaniem transakcji." },
    { category: 'finanse', question: "Macie integracje ze sklepami online?", answer: "Tak – rozwijamy moduły dla iAi. Dzięki nim obsługa sklepu stanie się prawie w 100% bezobsługowa." },
    { category: 'core', question: "Czym jest Perpetuum AI?", answer: "To nasz główny projekt – system, który ma generować dochód pasywny (min. 350 zł dziennie). Sam znajduje klientów, sprzedaje i obsługuje transakcje." },
    { category: 'core', question: "Do czego służy GridMaster AI?", answer: "To narzędzie do monitorowania i optymalizacji zużycia energii – np. w firmach produkcyjnych. AI pokazuje, gdzie tracisz pieniądze na prądzie." },
    { category: 'core', question: "Czym jest WindSynergy AI?", answer: "Rozwiązanie dla energetyki wiatrowej – wspiera planowanie i analizę opłacalności farm wiatrowych." },
    { category: 'edukacja', question: "Czy prowadzicie szkolenia?", answer: "Tak – np. Szkolenie AI dla Kancelarii, kursy o automatyzacji w e-commerce, czy warsztaty z n8n. Uczymy, jak używać AI w codziennym biznesie." },
    { category: 'edukacja', question: "Co to jest AI Quiz?", answer: "To prosty test online, który pokazuje, jak gotowa jest Twoja firma na automatyzację. W kilka minut wiesz, co można poprawić." },
    { category: 'edukacja', question: "Czym są Lasy AI?", answer: "To nasz manifest i projekt społeczny – łączymy AI z ekologią. Za każde wdrożenie wspieramy inicjatywy zalesiania i OZE." },
];

const FAQAccordionItem: React.FC<{ faq: typeof ALL_FAQS[0], isOpen: boolean, onClick: () => void, index: number }> = ({ faq, isOpen, onClick, index }) => {
    const contentId = `faq-content-${React.useId()}`;
    return (
        <RevealOnScroll delay={index * 30}>
            <div className={`border-b border-white/10 dark:border-white/5 ${isOpen ? 'bg-white/5 dark:bg-white/5' : ''} transition-colors duration-300 rounded-lg`}>
                <button
                    onClick={onClick}
                    className="w-full flex justify-between items-center py-5 px-4 text-left group"
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                >
                    <span className="font-semibold text-lg text-text-primary-light dark:text-text-primary-dark group-hover:text-primary-light dark:group-hover:text-primary-dark transition-colors">
                        {faq.question}
                    </span>
                    <div className={`w-8 h-8 rounded-lg bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center transition-all duration-300 ${isOpen ? 'rotate-180 bg-primary-light/20 dark:bg-primary-dark/20' : ''}`}>
                        <ChevronDown className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                    </div>
                </button>
                <div
                    id={contentId}
                    className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
                >
                    <p className="pb-5 px-4 text-text-secondary-light dark:text-text-secondary-dark">{faq.answer}</p>
                </div>
            </div>
        </RevealOnScroll>
    );
}

export const FaqPage: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(0);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const filteredFaqs = ALL_FAQS.filter(faq => {
        const matchesCategory = filter === 'all' || faq.category === filter;
        const matchesSearch = searchTerm === '' ||
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    usePageMetadata({
        title: "FAQ – Najczęściej Zadawane Pytania o Automatyzację AI",
        description: "Znajdź odpowiedzi na pytania o automatyzację AI, nasze produkty, proces wdrożenia i bezpieczeństwo danych. Sprawdź nasze FAQ i dowiedz się więcej.",
        schema: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": filteredFaqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        }
    });

    const handleToggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const categories = [
        { key: 'all', name: 'Wszystkie' },
        { key: 'general', name: 'Ogólne' },
        { key: 'agenci', name: 'Agenci & Obsługa' },
        { key: 'leady', name: 'Leady & Rynek' },
        { key: 'content', name: 'Content & Marketing' },
        { key: 'finanse', name: 'Finanse & Integracje' },
        { key: 'core', name: 'Projekty Specjalne' },
        { key: 'edukacja', name: 'Edukacja & Społeczność' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[50vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                            <HelpCircle className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            Najczęściej Zadawane Pytania
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto">
                            Wszystko co musisz wiedzieć o automatyzacji AI i naszych produktach.
                        </p>
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            {/* FAQ Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 max-w-4xl relative z-10">
                    {/* Search */}
                    <RevealOnScroll>
                        <GlassCard variant="medium" className="mb-8">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
                                <input
                                    id="faq-search"
                                    type="search"
                                    placeholder="Wyszukaj pytanie, np. 'Jak działa BiznesFinder'..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-white/5 border border-white/20 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none transition-all text-text-primary-light dark:text-text-primary-dark"
                                />
                            </div>
                        </GlassCard>
                    </RevealOnScroll>

                    {/* Category Filters */}
                    <RevealOnScroll delay={100}>
                        <div className="mb-8 flex flex-wrap gap-2" role="group" aria-label="Filtruj pytania po kategorii">
                            {categories.map(cat => (
                                <button
                                    key={cat.key}
                                    onClick={() => setFilter(cat.key)}
                                    aria-pressed={filter === cat.key}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${filter === cat.key
                                        ? 'bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark text-white shadow-lg'
                                        : 'bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10 hover:bg-white/20 dark:hover:bg-white/10 text-text-primary-light dark:text-text-primary-dark'
                                        }`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    </RevealOnScroll>

                    {/* FAQ List */}
                    <GlassCard variant="subtle" className="divide-y divide-white/10 dark:divide-white/5">
                        {filteredFaqs.length > 0 ? (
                            filteredFaqs.map((faq, index) => (
                                <FAQAccordionItem
                                    key={index}
                                    faq={faq}
                                    isOpen={openIndex === index}
                                    onClick={() => handleToggle(index)}
                                    index={index}
                                />
                            ))
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-text-secondary-light dark:text-text-secondary-dark" role="status">
                                    Nie znaleziono odpowiedzi na Twoje pytanie. Spróbuj użyć innych słów kluczowych lub skontaktuj się z nami!
                                </p>
                            </div>
                        )}
                    </GlassCard>
                </div>
            </section>
        </>
    );
};
